import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";

const DEFAULT_PREFS = {
  quietHoursStart: null, // e.g. "22:00"
  quietHoursEnd: null,   // e.g. "07:00"
  enablePlannerAlerts: true,
  enableShopAlerts: true,
  enableSyncAlerts: true,
};

function storageKey(userId) {
  return `mudo:notificationPrefs:${userId || "anon"}`;
}

function loadPrefs(userId) {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_PREFS, ...(parsed || {}) };
  } catch {
    return DEFAULT_PREFS;
  }
}

function savePrefs(userId, prefs) {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(prefs));
  } catch {
    // ignore storage errors
  }
}

function timeToMinutes(t) {
  if (!t || typeof t !== "string" || !t.includes(":")) return null;
  const [h, m] = t.split(":").map((n) => Number(n));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

export function isQuietNow(prefs, date = new Date()) {
  if (!prefs?.quietHoursStart || !prefs?.quietHoursEnd) return false;
  const start = timeToMinutes(prefs.quietHoursStart);
  const end = timeToMinutes(prefs.quietHoursEnd);
  if (start === null || end === null) return false;
  const minutes = date.getHours() * 60 + date.getMinutes();
  if (start < end) return minutes >= start && minutes < end;
  return minutes >= start || minutes < end;
}

export function useNotificationPrefs() {
  const { user } = useAuth() || {};
  const [prefs, setPrefs] = useState(() => loadPrefs(user?.id));

  useEffect(() => {
    setPrefs(loadPrefs(user?.id));
  }, [user?.id]);

  const updatePrefs = (patch) => {
    setPrefs((prev) => {
      const next = { ...prev, ...(typeof patch === "function" ? patch(prev) : patch) };
      savePrefs(user?.id, next);
      return next;
    });
  };

  const quiet = useMemo(() => isQuietNow(prefs), [prefs]);

  return { prefs, setPrefs: updatePrefs, isQuiet: quiet };
}
